import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface PageTransitionProps {
  children: React.ReactNode;
}

/**
 * PageTransition wrapper for smooth route transitions
 * - Uses fade + scale (transform/opacity only - GPU accelerated)
 * - No layout shift with preserved container
 * - Respects prefers-reduced-motion
 * - Double RAF for smooth paint timing
 */
export const PageTransition = ({ children }: PageTransitionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayChildren, setDisplayChildren] = useState(children);
  const location = useLocation();

  useEffect(() => {
    // Fade out current page
    setIsVisible(false);
    
    // Wait for exit animation, then update content
    const exitTimer = setTimeout(() => {
      setDisplayChildren(children);
      
      // Double RAF ensures smooth paint timing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    }, 150); // Half the transition duration for smoother feel

    return () => clearTimeout(exitTimer);
  }, [location.pathname]);

  return (
    <div
      className={cn(
        "motion-entrance motion-safe-transform",
        isVisible ? "opacity-100 scale-100" : "opacity-0 scale-[0.98]"
      )}
      style={{
        willChange: isVisible ? "auto" : "transform, opacity",
        // Preserve layout to prevent CLS
        minHeight: "50vh",
      }}
    >
      {displayChildren}
    </div>
  );
};
